import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

/** A member who paid but holds no policy number. */
export interface UnissuedMember {
  id: number;
  fullName: string | null;
  mobileNumber: string | null;
  email: string | null;
  kycStatus: string | null;
  paymentStatus: string | null;
  receiptNo: string | null;
  paidAt: string | null;
  partnerId: number | null;
  partnerName: string | null;
  clientId: number | null;
  clientName: string | null;
  masterPolicyId: number | null;
  masterPolicyNumber: string | null;
  seriesSuffix: string | null;
  seriesStartNo: number | null;
  seriesEndNo: number | null;
  /** Why it is stuck: 'KYC not verified' | 'No master policy on the member' | 'Ready to re-issue'. */
  reason: string | null;
}

/**
 * Paid-but-unissued members, and the action that fixes them.
 *
 * These are invisible in every other screen: the payment succeeded, so nothing looks wrong until
 * somebody asks why a certificate never arrived. The usual cause is a master policy whose number
 * series ran out or was configured with a range that does not fit.
 *
 * Scoped server-side — an admin sees every partner and client, a partner only its own members.
 */
@Injectable({ providedIn: 'root' })
export class PolicyIssueService {
  private http = inject(HttpClient);
  private readonly api = `${environment.apiUrl}/MemberPayment`;

  readonly rows = signal<UnissuedMember[]>([]);
  readonly loading = signal(false);
  /** Member id currently being issued, so the button can spin and block a double press. */
  readonly issuing = signal<number | null>(null);

  /** Refresh the list. Silent on failure — this is a background health check, not a page. */
  refresh(): void {
    this.loading.set(true);
    this.http.get<any>(`${this.api}/Unissued`).subscribe({
      next: (res) => {
        this.loading.set(false);
        this.rows.set(res?.success === false ? [] : (res?.data ?? []));
      },
      error: () => {
        this.loading.set(false);
        this.rows.set([]);
      },
    });
  }

  /**
   * Re-run issuance for one member. Idempotent on the server: pressing it twice reports the
   * existing number rather than minting a second one.
   */
  reissue(memberId: number) {
    return this.http.post<any>(`${this.api}/Reissue`, null, { params: { memberId } });
  }

  /** Only these can actually be fixed by pressing the button; the rest need KYC or a policy first. */
  readyCount(): number {
    return this.rows().filter((r) => this.isReady(r)).length;
  }

  isReady(r: UnissuedMember): boolean {
    return (r.kycStatus || '').toUpperCase() === 'VERIFIED' && !!r.masterPolicyId;
  }

  /** Drop a member from the list once it has been issued, without a full reload. */
  remove(memberId: number): void {
    this.rows.set(this.rows().filter((r) => r.id !== memberId));
  }
}
